/**
 * 재고관리 API 클라이언트 (Phase 7-2) — backend /stock/* 와 1:1.
 */

import type {
  ApiResponse,
  ItemCreateInput,
  ItemMasterRow,
  ItemUpdateInput,
  ReleasableStockRow,
  StockRow,
} from './types';

const BASE = '/api';

/** 재고 통계 (v_stock 전체) — 모든 권한 */
export async function listStock(): Promise<StockRow[]> {
  const res = await fetch(`${BASE}/stock`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<StockRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/* ── 숫자 드릴다운 (v2.78.0) — 주요재고현황의 숫자를 이루는 매입 행 ──────── */

export type BreakdownKind = 'pending' | 'incoming' | 'leased' | 'available';

export interface StockBreakdownRow {
  purchaseId: number;
  itemCode: string | null;
  description: string | null;
  supplier: string | null;
  qty: number;
  orderDate: string | null;
  actualInDate: string | null;
  actualOutDate: string | null;
  outType: string | null;
  salesId: string | null;
  projectName: string | null;
  endUser: string | null;
  /** 매출처 — 임대의 「어디에 나가 있나」가 여기 들어 있다 (재고관리 출고등록의 사용처) */
  actualSalesTo: string | null;
  salesStatus: string | null;
  note: string | null;
}

export interface StockBreakdown {
  itemCode: string;
  kind: BreakdownKind;
  itemName: string;
  safeStock: number;
  currentStock: number;
  /** 화면 숫자 — 목록 합계와 같아야 한다 (서버 검사 verify:stock-drill 이 전 품목 대조) */
  total: number;
  rows: StockBreakdownRow[];
}

export async function getStockBreakdown(
  itemCode: string,
  kind: BreakdownKind,
): Promise<StockBreakdown> {
  const qs = new URLSearchParams({ itemCode, kind });
  const res = await fetch(`${BASE}/stock/breakdown?${qs.toString()}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<StockBreakdown>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface ItemSearchParams {
  q?: string;
  category?: 'HW' | 'SW' | 'Service';
  keyOnly?: boolean;
}

/** 품명&품목 마스터 검색 */
export async function listItems(params: ItemSearchParams = {}): Promise<ItemMasterRow[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.category) qs.set('category', params.category);
  if (params.keyOnly) qs.set('keyOnly', 'true');
  const res = await fetch(`${BASE}/stock/items?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<ItemMasterRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/**
 * 품목별 출고가능 수량 — 승인 때 실제 차감되는 기준(FIFO 후보)과 동일.
 * salesId 를 넘기면 그 영업건에 예약된 잔량(reserved)도 함께 계산해 준다.
 */
export async function listReleasable(
  salesId?: string | null,
): Promise<ReleasableStockRow[]> {
  const qs = new URLSearchParams();
  if (salesId) qs.set('salesId', salesId);
  const res = await fetch(`${BASE}/stock/releasable?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<ReleasableStockRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function createItem(input: ItemCreateInput): Promise<string> {
  const res = await fetch(`${BASE}/stock/items`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ itemCode: string }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.itemCode;
}

export async function updateItem(
  itemCode: string,
  input: ItemUpdateInput,
): Promise<void> {
  const res = await fetch(
    `${BASE}/stock/item?code=${encodeURIComponent(itemCode)}`,   // ⭐코드는 물음표 뒤로(v2.225.1 · 경로면 nginx 가 빗금을 풀어 404)
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  const json = (await res.json()) as ApiResponse<{ itemCode: string }>;
  if (!json.ok) throw new Error(json.error.message);
}

/**
 * 주요재고 묶음 지정·해제 — [주요재고 수정] 화면의 [저장].
 *
 * 낱개 PATCH 를 반복하지 않고 한 번에 보낸다. 서버가 트랜잭션으로 처리하므로
 * 하나라도 실패하면 전부 되돌아간다(절반만 반영되는 상태가 없다).
 */
export interface KeyStockChange {
  itemCode: string;
  isKeyStock?: boolean;
  safeStock?: number;
}

export async function saveKeyStock(
  changes: KeyStockChange[],
): Promise<{ turnedOn: number; turnedOff: number; safeOnly: number }> {
  const res = await fetch(`${BASE}/stock/key-items`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ changes }),
  });
  const json = (await res.json()) as ApiResponse<{
    turnedOn: number;
    turnedOff: number;
    safeOnly: number;
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function deleteItem(itemCode: string): Promise<void> {
  const res = await fetch(
    `${BASE}/stock/item?code=${encodeURIComponent(itemCode)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{ itemCode: string }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ── 보유재고 자산 (주요재고 현재고의 매입원가 합계) ───────────────
export interface InventoryAssetItem {
  itemCode: string;
  itemName: string | null;
  itemGroup: string | null;
  displayGroup: string | null;
  qty: number;
  amount: number;
}
export interface InventoryAsset {
  items: InventoryAssetItem[];
  total: { itemCount: number; qty: number; amount: number };
}

/** 보유재고 자산 — sales 이상만 (매입 원가 노출). viewer 차단. */
export async function getInventoryAsset(): Promise<InventoryAsset> {
  const res = await fetch(`${BASE}/stock/asset`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<InventoryAsset>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface RecentSupplierInfo {
  supplier: string;
  orderDate: string | null;
  recentPrice: number; // 최근 1건 매입 단가
  avgPrice: number;    // 전체 평균 매입 단가 (단가>0 행만)
  count: number;       // 매입 건수
}

/** 매입내역에서 해당 itemCode 의 최근 매입처 + 매입가 통계 fetch (BOM 자동 채움용) */
export async function fetchRecentSupplier(itemCode: string): Promise<RecentSupplierInfo> {
  const res = await fetch(
    `${BASE}/stock/item/recent-supplier?code=${encodeURIComponent(itemCode)}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<RecentSupplierInfo>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
